import { isNoteLinkTarget, noteLinkHref } from './note-link.ts';

function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function escapeAttribute(str) {
  return escapeHtml(str).replace(/`/g, '&#096;');
}

// 行頭の `[ ]` / `[v]` / `[x]`。箇条書きの記号（`- [ ]`）と字下げは前置きとして許す。
// 描画（formatMarkdownInlineText）・切り替え（toggleMarkdownCheckbox）・判定が同じ規則を使う。
const markdownCheckboxPattern = /(^|\n)([ \t]*(?:[-*+][ \t]+)?)\[([ vx])\](?=\s|$)/g;
// 1行だけを見る版。フェンスや表の手前に、いくつチェックボックスがあるかを数えるために使う。
const markdownCheckboxLinePattern = /^[ \t]*(?:[-*+][ \t]+)?\[[ vx]\](?=\s|$)/;

export function hasMarkdownCheckbox(str) {
  markdownCheckboxPattern.lastIndex = 0;
  return markdownCheckboxPattern.test(String(str || ''));
}

// checkboxIndex は、本文全体の中で上から数えた何個目のチェックボックスか。
export function toggleMarkdownCheckbox(str, checkboxIndex) {
  let currentIndex = 0;
  markdownCheckboxPattern.lastIndex = 0;
  return String(str || '').replace(markdownCheckboxPattern, (raw, lineStart, lead, mark) => {
    if (currentIndex++ !== checkboxIndex) {
      return raw;
    }
    return `${lineStart}${lead}[${mark === ' ' ? 'v' : ' '}]`;
  });
}

function formatMarkdownCheckbox(mark, index) {
  const checked = mark !== ' ';
  const marker = `[${mark}]`;
  const label = checked ? 'チェック済み' : '未チェック';
  const checkPath = checked ? '<path class="md-checkbox-check" d="M6.4 12.2l3.4 3.4 7.8-8"/>' : '';
  return (
    `<span class="md-checkbox${checked ? ' is-checked' : ''}" role="checkbox" aria-checked="${checked}" aria-label="${label}" data-md-checkbox-index="${index}" contenteditable="false">` +
    `<span class="md-checkbox-source" aria-hidden="true">${marker}</span>` +
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<rect class="md-checkbox-box" x="3.5" y="3.5" width="17" height="17" rx="5"/>' +
    checkPath +
    '</svg></span>'
  );
}

// ノード全体が区切り線（5個以上の `-` または `ー`）のとき。
// `---` は行末に打つとノード補完プラグインが働く記法なので、3個では区切り線にしない。
export function isMarkdownHorizontalRule(str) {
  return /^(?:-{5,}|ー{5,})$/.test(String(str || ''));
}

// 本文のどこかに、区切り線だけの行があるか。
export function hasMarkdownRuleLine(str) {
  return String(str || '')
    .split(/\r?\n/)
    .some((line) => isMarkdownHorizontalRule(line));
}

function isSafeMarkdownUrl(url) {
  const value = String(url || '').trim();
  if (value === '') {
    return false;
  }

  const schemeMatch = value.match(/^([a-z][a-z0-9+.-]*):/i);
  if (!schemeMatch) {
    return true;
  }

  return ['http:', 'https:', 'mailto:', 'tel:'].includes(`${schemeMatch[1].toLowerCase()}:`);
}

function formatSingleAutoLink(url) {
  let cleanUrl = url;
  let suffix = '';

  const trailingPunctuation = /([.,:;!?\)]+)$/;
  const matchPunc = cleanUrl.match(trailingPunctuation);
  if (matchPunc) {
    const punc = matchPunc[1];
    const openCount = (cleanUrl.match(/\(/g) || []).length;
    const closeCount = (cleanUrl.match(/\)/g) || []).length;

    if (punc === ')' && openCount >= closeCount) {
      // Parentheses matched
    } else {
      cleanUrl = cleanUrl.slice(0, -punc.length);
      suffix = punc;
    }
  }

  const href = cleanUrl.replace(/`/g, '&#096;');
  return `<a class="md-link auto-link" href="${href}" data-md-link="1" rel="noopener noreferrer">${cleanUrl}</a>${suffix}`;
}

function formatSingleEmailLink(email) {
  let cleanEmail = email;
  let suffix = '';

  const trailingPunctuation = /([.,:;!?\)]+)$/;
  const matchPunc = cleanEmail.match(trailingPunctuation);
  if (matchPunc) {
    const punc = matchPunc[1];
    cleanEmail = cleanEmail.slice(0, -punc.length);
    suffix = punc;
  }

  const href = `mailto:${cleanEmail}`.replace(/`/g, '&#096;');
  return `<a class="md-link auto-link" href="${href}" data-md-link="1" rel="noopener noreferrer">${cleanEmail}</a>${suffix}`;
}

function formatPlainMarkdown(str) {
  const codeBlocks = [];
  const urls = [];
  const emails = [];

  let escaped = escapeHtml(str);

  // 引用記号の処理
  escaped = escaped.replace(/^(&gt;|＞)(\s)/, '<span class="md-marker">$1</span>$2');

  // インラインコードを退避
  const codePattern = /`([^`]+)`/g;
  escaped = escaped.replace(codePattern, (match, codeContent) => {
    codeBlocks.push(codeContent);
    return `\u0000CODE_${codeBlocks.length - 1}\u0000`;
  });

  // URLを退避
  const urlPattern = /(https?:\/\/[^\s"<>]+)/g;
  escaped = escaped.replace(urlPattern, (match) => {
    urls.push(match);
    return `\u0000URL_${urls.length - 1}\u0000`;
  });

  // メールアドレスを退避
  const emailPattern = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
  escaped = escaped.replace(emailPattern, (match) => {
    emails.push(match);
    return `\u0000EMAIL_${emails.length - 1}\u0000`;
  });

  // 聖書リンクとタグリンクの処理
  let html = formatTextWithScriptureAndTagLinks(escaped);

  // 太字の処理
  html = html.replace(
    /\*\*([^*]+)\*\*/g,
    '<strong><span class="md-marker md-strong-marker">**</span>$1<span class="md-marker md-strong-marker">**</span></strong>'
  );

  // メールアドレスの復元
  html = html.replace(/\u0000EMAIL_(\d+)\u0000/g, (_, indexStr) => {
    const idx = parseInt(indexStr, 10);
    const rawEmail = emails[idx];
    return formatSingleEmailLink(rawEmail);
  });

  // URLの復元（自動リンク化）
  html = html.replace(/\u0000URL_(\d+)\u0000/g, (_, indexStr) => {
    const idx = parseInt(indexStr, 10);
    const rawUrl = urls[idx];
    return formatSingleAutoLink(rawUrl);
  });

  // インラインコードの復元
  html = html.replace(/\u0000CODE_(\d+)\u0000/g, (_, indexStr) => {
    const idx = parseInt(indexStr, 10);
    const codeContent = codeBlocks[idx];
    return '<code><span class="md-marker">`</span>' + codeContent + '<span class="md-marker">`</span></code>';
  });

  return html;
}

let scripturePattern = /(?!)/;

function escapeRegExpAlternatives(values) {
  return values
    .slice()
    .sort((a, b) => b.length - a.length)
    .map((value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');
}

export function updateScripturePattern(names, codes, singleChapterNames = [], singleChapterCodes = []) {
  const scriptureBookPattern = escapeRegExpAlternatives(names.concat(codes));
  // 聖句の範囲には、通常のハイフンのほかに聖書からコピーした en dash
  // が使われることがある。章をまたぐ範囲も、後ろの節まで含めて一つの
  // 参照として認識する（例: 民数記 13:1–14:12，20-38）。
  const verseRangePattern = '\\d{1,3}(?:\\s*[-‐‑‒–—〜～－]\\s*(?!\\d{1,3}\\s*[:：])\\d{1,3})?';
  const versePattern = `${verseRangePattern}(?:\\s*[,，、]\\s*${verseRangePattern})*`;
  const chapterVersePattern = `\\d{1,3}\\s*(?::|：)\\s*${versePattern}`;
  const crossChapterVersePattern = `${chapterVersePattern}(?:\\s*[-‐‑‒–—〜～－]\\s*${chapterVersePattern})?(?:\\s*[,，、]\\s*${verseRangePattern})*`;
  const japaneseChapterVersePattern = `\\d{1,3}\\s*章\\s*${versePattern}\\s*節`;
  // 章と節（例: マタイ7:7）
  // セミコロン区切りで別の章へ続く表記（例: コリント第一 4:6; 8:12，13）も含む。
  // 範囲の途中にある「14:12」を、先頭の「13:1–14」だけで終わった
  // 参照として先に拾わないようにする。
  const incompleteCrossChapterPattern = '(?!\\s*[-‐‑‒–—〜～－]\\s*\\d{1,3}\\s*[:：])';
  let referencePattern = `(?:${scriptureBookPattern})\\s*(?:${crossChapterVersePattern}|${japaneseChapterVersePattern})${incompleteCrossChapterPattern}(?:\\s*[;；]\\s*${chapterVersePattern})*`;

  // 章が1つしかない書（オバデヤ、フィレモン、ヨハネ第二、ヨハネ第三、ユダなど）は、
  // 「書名 節」のように章番号を省略して書かれることがある。
  const singleChapterBookPattern = escapeRegExpAlternatives(singleChapterNames.concat(singleChapterCodes));
  if (singleChapterBookPattern !== '') {
    referencePattern += `|(?:${singleChapterBookPattern})\\s*${versePattern}`;
  }

  scripturePattern = new RegExp(`(^|[^A-Za-z0-9])((?:${referencePattern}))`, 'gi');
}

const tagPattern = /(^|[^\p{L}\p{N}_-])#([\p{L}\p{N}_-]+)/gu;
const datetimeMarkerPattern = /!!\d{4}[-/]\d{2}[-/]\d{2}(?:T\d{2}:\d{2}:\d{2}(?:[+-]\d{2}:\d{2}|Z)?)?/g;

export function extractTags(text) {
  const source = String(text || '');

  const urlPattern = /(https?:\/\/[^\s"<>]+)/g;
  const codePattern = /`([^`]+)`/g;

  const cleaned = source.replace(codePattern, '').replace(urlPattern, '');

  const tags = [];
  let match;
  tagPattern.lastIndex = 0;

  while ((match = tagPattern.exec(cleaned)) !== null) {
    const tag = match[2];
    if (tag && !tags.includes(tag)) {
      tags.push(tag);
    }
  }

  return tags;
}

export function hasDatetimeMarker(text) {
  datetimeMarkerPattern.lastIndex = 0;
  return datetimeMarkerPattern.test(String(text || ''));
}

function formatTextWithTagLinks(str) {
  const source = String(str || '');
  let html = '';
  let lastIndex = 0;
  let match;
  tagPattern.lastIndex = 0;

  while ((match = tagPattern.exec(source)) !== null) {
    const prefix = match[1] || '';
    const tag = match[2];
    const hashIndex = match.index + prefix.length;
    html += source.slice(lastIndex, hashIndex);
    html += `<button class="tag-inline" type="button" data-tag="${escapeAttribute(tag)}" contenteditable="false">#${escapeHtml(tag)}</button>`;
    lastIndex = hashIndex + tag.length + 1;
  }

  html += source.slice(lastIndex);
  return html;
}

function formatTextWithDatetimeAndTagLinks(str) {
  const source = String(str || '');
  let html = '';
  let lastIndex = 0;
  let match;
  datetimeMarkerPattern.lastIndex = 0;

  while ((match = datetimeMarkerPattern.exec(source)) !== null) {
    const marker = match[0];
    html += formatTextWithTagLinks(source.slice(lastIndex, match.index));
    html += `<span class="md-date-marker">${escapeHtml(marker)}</span>`;
    lastIndex = match.index + marker.length;
  }

  html += formatTextWithTagLinks(source.slice(lastIndex));
  return html;
}

function normalizeScriptureQuery(query) {
  return query
    .replace(
      /^(.+?)\s*(\d{1,3})\s*章\s*(\d{1,3}(?:\s*[-‐‑‒–—〜～]\s*\d{1,3})?)\s*節$/,
      (_, book, chapter, verses) => `${book.trim()} ${chapter}:${verses}`
    )
    .replace(/[-‐‑‒–—〜～－]/g, '-');
}

function formatScriptureLink(label, query = label) {
  const normalizedQuery = normalizeScriptureQuery(query);
  const normalizedHref = `https://wol.jw.org/ja/wol/l/r7/lp-j?q=${encodeURIComponent(normalizedQuery)}`;
  return `<a class="md-link scripture-link" href="${escapeAttribute(normalizedHref)}" data-md-link="1" rel="noopener noreferrer">${escapeHtml(label)}</a>`;
}

function formatCrossChapterScriptureLinks(keyword) {
  const match = keyword.match(
    /^(.+?)(\d{1,3}\s*[:：]\s*[\d\s,，、\-‐‑‒–—〜～－]+?)(\s*[-‐‑‒–—〜～－]\s*)(\d{1,3}\s*[:：]\s*[\d\s,，、\-‐‑‒–—〜～－]+)(.*)$/
  );
  if (!match) {
    return formatScriptureLink(keyword);
  }

  const book = match[1];
  const firstReference = match[2];
  const separator = match[3];
  const secondReference = match[4];
  const suffix = match[5];
  const bookQuery = book.trim();
  const normalizeQuery = (reference) => reference.replace(/：/g, ':').replace(/\s+/g, ' ').trim();

  let suffixHtml = escapeHtml(suffix);
  const suffixParts = suffix.match(/^(\s*[;；]\s*)(.*)$/);
  if (suffixParts) {
    const remaining = suffixParts[2].split(/(\s*[;；]\s*)/);
    suffixHtml = escapeHtml(suffixParts[1]);
    for (let index = 0; index < remaining.length; index += 2) {
      const reference = remaining[index].trim();
      if (!reference) continue;
      const separator = index === 0 ? '' : remaining[index - 1];
      const referenceMatch = reference.match(/^\d{1,3}\s*[:：]\s*[\d\s,，、\-‐‑‒–—〜～－]+$/);
      suffixHtml += escapeHtml(separator);
      suffixHtml += referenceMatch
        ? formatScriptureLink(reference, `${bookQuery} ${normalizeQuery(reference)}`)
        : escapeHtml(reference);
    }
  }

  return (
    formatScriptureLink(`${book}${firstReference}`, `${bookQuery} ${normalizeQuery(firstReference)}`) +
    escapeHtml(separator) +
    formatScriptureLink(secondReference, `${bookQuery} ${normalizeQuery(secondReference)}`) +
    suffixHtml
  );
}

function formatCompositeScriptureLinks(keyword) {
  // 章をまたぐ参照を章ごとに分解すると、後半の節を先頭章として
  // 検索してしまう（例: 13:1-14:12，20-38）。セミコロンで別の
  // 章が続く場合も含め、検索サービスが参照全体を解釈できるように
  // そのまま一つのリンクにする。
  if (/\d{1,3}\s*[:：]\s*[\d\s,，、\-‐‑‒–—〜～－]+\s*[-‐‑‒–—〜～－]\s*\d{1,3}\s*[:：]/.test(keyword)) {
    return formatCrossChapterScriptureLinks(keyword);
  }

  const chapterParts = keyword.split(/(\s*[;；]\s*)/);
  if (chapterParts.length === 1) {
    return formatScriptureLink(keyword);
  }

  const firstChapter = chapterParts[0].match(/^(.*?)(\d{1,3}):(.*)$/);
  if (!firstChapter) {
    return formatScriptureLink(keyword);
  }

  const bookLabel = firstChapter[1];
  const bookQuery = bookLabel.trim();
  let html = '';

  for (let index = 0; index < chapterParts.length; index += 2) {
    const chapterPart = chapterParts[index];
    const chapterMatch = index === 0 ? firstChapter : chapterPart.match(/^(\d{1,3}):(.*)$/);
    if (!chapterMatch) {
      return formatScriptureLink(keyword);
    }

    const chapter = index === 0 ? chapterMatch[2] : chapterMatch[1];
    const verseSpec = index === 0 ? chapterMatch[3] : chapterMatch[2];
    const visiblePrefix = index === 0 ? `${bookLabel}${chapter}:` : `${chapter}:`;
    const verseParts = verseSpec.split(/(\s*[,，、]\s*)/);

    html += formatScriptureLink(`${visiblePrefix}${verseParts[0]}`, `${bookQuery} ${chapter}:${verseParts[0].trim()}`);
    for (let verseIndex = 1; verseIndex < verseParts.length; verseIndex += 2) {
      html += escapeHtml(verseParts[verseIndex]);
      html += formatScriptureLink(
        verseParts[verseIndex + 1],
        `${bookQuery} ${chapter}:${verseParts[verseIndex + 1].trim()}`
      );
    }

    if (index + 1 < chapterParts.length) {
      html += escapeHtml(chapterParts[index + 1]);
    }
  }
  return html;
}

function formatTextWithScriptureAndTagLinks(str) {
  const source = String(str || '');
  let html = '';
  let lastIndex = 0;
  let match;
  scripturePattern.lastIndex = 0;

  while ((match = scripturePattern.exec(source)) !== null) {
    const prefix = match[1] || '';
    const keyword = match[2];
    const keywordIndex = match.index + prefix.length;
    html += formatTextWithDatetimeAndTagLinks(source.slice(lastIndex, keywordIndex));
    html += formatCompositeScriptureLinks(keyword);
    lastIndex = keywordIndex + keyword.length;
  }

  html += formatTextWithDatetimeAndTagLinks(source.slice(lastIndex));
  return html;
}

// 行単位の装飾（見出し・箇条書き・区切り線）。アウトライン・カード・公開画面が同じ関数を通す。
//
// 行の先頭の記号を `\u0000MD_BLOCK_n\u0000` に、行末を `\u0000MD_BLOCK_END\u0000` に置き換えておき、
// リンクや太字などの行内の整形が済んだあとで HTML に戻す。行を先に切り分けて個別に整形すると、
// 複数行にまたがるコード・チェックボックスの通し番号・リンクの判定がずれるため。
//
// 元の記号（`## ` や `- `）は消さず、`md-marker` などのクラスを付けた要素に残す。
// 編集中に本文を読み戻したとき（textContent）、原文と同じになるようにするため。
// 見た目（記号を隠す・箇条書きの点を描く）は CSS が決める。
interface MarkdownBlockKinds {
  headings: boolean;
  lists: boolean;
  rules: boolean;
}

const headingLinePattern = /^(#{1,6})([ \t]+)(.*)$/;
const listLinePattern = /^([ \t]*)([-*+]|\d{1,9}[.)])([ \t]+)(.*)$/;
const blockEndToken = '\u0000MD_BLOCK_END\u0000';

// 字下げの深さ。タブは2文字ぶん、空白2文字で1段。深すぎる入れ子は6段で止める。
function listIndentLevel(indent) {
  let width = 0;
  for (const char of indent) {
    width += char === '\t' ? 2 : 1;
  }
  return Math.min(Math.floor(width / 2), 6);
}

function decorateBlockLines(source: string, kinds: MarkdownBlockKinds, openings: string[]): string {
  const openToken = (html: string) => {
    openings.push(html);
    return `\u0000MD_BLOCK_${openings.length - 1}\u0000`;
  };
  const lines = source.split('\n');
  const out: string[] = [];

  lines.forEach((rawLine, index) => {
    const separator = index < lines.length - 1 ? '\n' : '';
    const cr = rawLine.endsWith('\r') ? '\r' : '';
    const line = cr ? rawLine.slice(0, -1) : rawLine;

    const heading = kinds.headings ? headingLinePattern.exec(line) : null;
    if (heading) {
      // 見出しは行内の装飾なので、改行は要素の外に置く
      const open = openToken(
        `<span class="md-heading-line md-heading-${heading[1].length}"><span class="md-marker">${heading[1]}${heading[2]}</span>`
      );
      out.push(`${open}${heading[3]}${blockEndToken}${cr}${separator}`);
      return;
    }

    // 区切り線と箇条書きは、行そのものが1つの塊（display: block）になる。
    // 塊の直後の改行は空行として描かれてしまうため、改行は塊の中に入れる。
    if (kinds.rules && isMarkdownHorizontalRule(line)) {
      out.push(`${openToken('<span class="md-rule-line">')}${line}${cr}${separator}${blockEndToken}`);
      return;
    }

    const list = kinds.lists ? listLinePattern.exec(line) : null;
    if (list) {
      const [, indent, marker, gap, content] = list;
      const ordered = /^\d/.test(marker);
      // 先頭がチェックボックスの行は、箇条書きの点を出さない（チェックボックスが点の代わり）
      const task = content.startsWith('\u0000MD_CHECKBOX_');
      const classes = `md-list-line${ordered ? ' md-list-ordered' : ''}${task ? ' md-list-task' : ''}`;
      const open = openToken(
        `<span class="${classes}" style="--md-list-level:${listIndentLevel(indent)}">` +
          (indent === '' ? '' : `<span class="md-list-indent">${indent}</span>`) +
          `<span class="md-list-marker ${ordered ? 'md-list-number' : 'md-list-bullet'}">${escapeHtml(marker)}${gap}</span>`
      );
      out.push(`${open}${content}${cr}${separator}${blockEndToken}`);
      return;
    }

    out.push(`${rawLine}${separator}`);
  });

  return out.join('');
}

function formatMarkdownInlineText(
  str,
  options: {
    inlineImages?: boolean;
    decorateCheckboxes?: boolean;
    blockLines?: boolean;
    decorateHeadingLines?: boolean;
    checkboxBase?: number;
  } = {}
) {
  const inlineImages = options.inlineImages ?? true;
  const source = String(str || '');
  const codeBlocks: string[] = [];
  const checkboxes: string[] = [];

  // インラインコードを退避
  const codePattern = /`([^`]+)`/g;
  let sourceWithPlaceholders = source.replace(codePattern, (match, codeContent) => {
    codeBlocks.push(escapeHtml(codeContent));
    return `\u0000MD_CODE_${codeBlocks.length - 1}\u0000`;
  });

  if (options.decorateCheckboxes !== false) {
    markdownCheckboxPattern.lastIndex = 0;
    sourceWithPlaceholders = sourceWithPlaceholders.replace(markdownCheckboxPattern, (_, lineStart, lead, mark) => {
      checkboxes.push(mark);
      return `${lineStart}${lead}\u0000MD_CHECKBOX_${checkboxes.length - 1}\u0000`;
    });
  }

  // 見出し・箇条書き・区切り線。チェックボックスの置き換えのあとに行う（箇条書きの行が
  // チェックボックスで始まるかどうかを、置き換え後の目印で判定するため）。
  const blockKinds: MarkdownBlockKinds = {
    headings: options.blockLines === true || options.decorateHeadingLines === true,
    lists: options.blockLines === true,
    rules: options.blockLines === true,
  };
  const blockOpenings: string[] = [];
  if (blockKinds.headings || blockKinds.lists || blockKinds.rules) {
    sourceWithPlaceholders = decorateBlockLines(sourceWithPlaceholders, blockKinds, blockOpenings);
  }

  const pattern = /(!)?\[([^\]\n]+)\]\(([^)\n]+)\)/g;
  let html = '';
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(sourceWithPlaceholders)) !== null) {
    const [raw, isImage, label, rawUrl] = match;
    const url = rawUrl.trim();
    html += formatPlainMarkdown(sourceWithPlaceholders.slice(lastIndex, match.index));

    // 空白を含むリンク先は、ノート名（空白入りの名前）だけを許す。
    // 空白を含む外部URLや画像URLは従来どおりリンクにせず、原文のまま出す。
    const hasUnsafeSpace = isImage ? /\s/.test(rawUrl) : /\s/.test(url) && !isNoteLinkTarget(url);

    if (!hasUnsafeSpace && isSafeMarkdownUrl(url)) {
      if (isImage) {
        html += '<span class="md-marker md-image-marker">![</span>';
        html += `<a class="md-link md-image-link" href="${escapeAttribute(url)}" data-md-img-link="1" rel="noopener noreferrer">${escapeHtml(label)}</a>`;
        html += '<span class="md-marker md-image-marker">](</span>';
        html += `<a class="md-link md-image-url-link" href="${escapeAttribute(url)}" data-md-img-link="1" rel="noopener noreferrer">${escapeHtml(url)}</a>`;
        html += '<span class="md-marker md-image-marker">)</span>';
        if (inlineImages) {
          // ラベルは alt / 本文テキストとして持たせない。alt があると画像の読み込みに
          // 失敗したときにラベルが本文の続きのように表示され、キャプションを子要素の
          // テキストにすると contenteditable の textContent にラベルが混ざってしまう。
          // 表示用のラベルは data 属性に入れ、キャプションは CSS の content で描画する。
          html += `<div class="md-image-preview-container" contenteditable="false">`;
          html += `<img class="md-image-preview" src="${escapeAttribute(url)}" alt="" data-md-zoomable-img="1" data-md-image-label="${escapeAttribute(label)}">`;
          html += `<div class="md-image-caption" data-md-image-label="${escapeAttribute(label)}"></div>`;
          html += `</div>`;
        }
      } else if (isNoteLinkTarget(url)) {
        // 自分の別のノートへのリンク。href は note_name 付きの相対 URL にする。
        html += '<span class="md-marker md-link-marker">[</span>';
        html += `<a class="md-link note-link" href="${escapeAttribute(noteLinkHref(url))}" data-md-link="1" data-note-link="1" data-note-name="${escapeAttribute(url)}" rel="noopener noreferrer">${escapeHtml(label)}</a>`;
        // 括弧内は前後の空白も含めて原文どおり出し、textContent から原文を復元できるようにする。
        html += `<span class="md-marker md-link-marker">](${escapeHtml(rawUrl)})</span>`;
      } else {
        let href = url;
        if (!/^[a-z][a-z0-9+.-]*:/i.test(href) && /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(href)) {
          href = `mailto:${href}`;
        }
        html += '<span class="md-marker md-link-marker">[</span>';
        html += `<a class="md-link" href="${escapeAttribute(href)}" data-md-link="1" rel="noopener noreferrer">${escapeHtml(label)}</a>`;
        html += `<span class="md-marker md-link-marker">](${escapeHtml(rawUrl)})</span>`;
      }
    } else {
      html += formatPlainMarkdown(raw);
    }

    lastIndex = match.index + raw.length;
  }

  html += formatPlainMarkdown(sourceWithPlaceholders.slice(lastIndex));

  // インラインコードを復元
  html = html.replace(/\u0000MD_CODE_(\d+)\u0000/g, (_, indexStr) => {
    const idx = parseInt(indexStr, 10);
    const codeContent = codeBlocks[idx];
    return '<code><span class="md-marker">`</span>' + codeContent + '<span class="md-marker">`</span></code>';
  });

  // 通し番号は本文全体で数える（フェンスや表で本文を分けても、切り替えの対象がずれないように）
  const checkboxBase = options.checkboxBase ?? 0;
  html = html.replace(/\u0000MD_CHECKBOX_(\d+)\u0000/g, (_, indexStr) => {
    const index = parseInt(indexStr, 10);
    return formatMarkdownCheckbox(checkboxes[index], checkboxBase + index);
  });

  html = html
    .replace(/\u0000MD_BLOCK_(\d+)\u0000/g, (_, indexStr) => blockOpenings[parseInt(indexStr, 10)] ?? '')
    .replace(/\u0000MD_BLOCK_END\u0000/g, '</span>');

  return html;
}

const mermaidFenceOpenPattern = /^(```|:::)\s*mermaid\s*$/i;
const delimitedFenceOpenPattern = /^(```|:::)\s*(csv|tsv)\s*$/i;

function mermaidFenceMatch(line) {
  return mermaidFenceOpenPattern.exec(String(line || '').trim());
}

function isMatchingFenceClose(line, marker) {
  return String(line || '').trim() === marker;
}

export function hasMermaidBlock(str) {
  const lines = String(str || '').split(/\r?\n/);
  for (let index = 0; index < lines.length; index += 1) {
    const match = mermaidFenceMatch(lines[index]);
    if (!match) continue;
    for (let closeIndex = index + 1; closeIndex < lines.length; closeIndex += 1) {
      if (isMatchingFenceClose(lines[closeIndex], match[1])) {
        return true;
      }
    }
  }
  return false;
}

function formatMermaidBlock(source) {
  const encoded = encodeURIComponent(source);
  return (
    `<div class="md-mermaid" contenteditable="false" data-mermaid-pending="1" data-mermaid-source="${encoded}">` +
    `<div class="md-mermaid-placeholder">Mermaid</div></div>`
  );
}

// ```lang 〜 ``` のコードブロック。mermaid / csv / tsv は専用ブロックとして先に処理するので、
// ここへ来るのは表示用に整形しないコード（sh、text、nako3 など）だけになる。
const codeFenceOpenPattern = /^```([A-Za-z0-9_+#.-]*)\s*$/;

function codeFenceMatch(line) {
  return codeFenceOpenPattern.exec(String(line || '').trim());
}

export function hasCodeBlock(str) {
  return String(str || '')
    .split(/\r?\n/)
    .some((line) => codeFenceMatch(line) !== null);
}

function formatCodeBlock(language, lines) {
  const lang = String(language || '').trim();
  const langKey = lang.toLowerCase();
  const label = lang === '' ? '' : `<div class="md-code-lang" aria-hidden="true">${escapeHtml(lang)}</div>`;
  const codeClass = lang === '' ? 'md-code-text' : `md-code-text language-${escapeAttribute(langKey)}`;
  return (
    `<div class="md-code-block" contenteditable="false" data-code-lang="${escapeAttribute(langKey)}">` +
    label +
    `<pre class="md-code"><code class="${codeClass}">${escapeHtml(lines.join('\n'))}</code></pre>` +
    '</div>'
  );
}

export function parseDelimitedTable(source, delimiter) {
  let text = String(source || '').replace(/\r\n/g, '\n');
  if (text.startsWith('\uFEFF')) {
    text = text.slice(1);
  }
  if (text === '' || text.includes('\r')) {
    return null;
  }

  const records = [];
  let record = [];
  let field = '';
  let inQuotes = false;
  let closedQuote = false;

  const finishField = () => {
    record.push(field);
    field = '';
    closedQuote = false;
  };
  const finishRecord = () => {
    finishField();
    records.push(record);
    record = [];
  };

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (inQuotes) {
      if (char === '"') {
        if (text[index + 1] === '"') {
          field += '"';
          index += 1;
        } else {
          inQuotes = false;
          closedQuote = true;
        }
      } else {
        field += char;
      }
      continue;
    }

    if (closedQuote) {
      if (char === delimiter) {
        finishField();
      } else if (char === '\n') {
        finishRecord();
      } else {
        return null;
      }
      continue;
    }

    if (char === '"') {
      if (field !== '') {
        return null;
      }
      inQuotes = true;
    } else if (char === delimiter) {
      finishField();
    } else if (char === '\n') {
      finishRecord();
    } else {
      field += char;
    }
  }

  if (inQuotes) {
    return null;
  }
  finishRecord();

  const columnCount = records[0].length;
  if (columnCount === 0 || records.some((row) => row.length !== columnCount)) {
    return null;
  }
  return { header: records[0], rows: records.slice(1) };
}

function delimitedFenceMatch(line) {
  return delimitedFenceOpenPattern.exec(String(line || '').trim());
}

function isDelimitedFenceClose(line, marker) {
  return isMatchingFenceClose(line, marker);
}

function hasDelimitedFence(str) {
  return String(str || '')
    .split(/\r?\n/)
    .some((line) => delimitedFenceMatch(line) !== null);
}

export function hasDelimitedTableBlock(str) {
  return hasDelimitedFence(str);
}

function formatDelimitedScriptureText(value) {
  const source = String(value || '');
  let html = '';
  let lastIndex = 0;
  let match;
  scripturePattern.lastIndex = 0;
  while ((match = scripturePattern.exec(source)) !== null) {
    const prefix = match[1] || '';
    const keyword = match[2];
    const keywordIndex = match.index + prefix.length;
    html += escapeHtml(source.slice(lastIndex, keywordIndex));
    html += formatCompositeScriptureLinks(keyword);
    lastIndex = keywordIndex + keyword.length;
  }
  return html + escapeHtml(source.slice(lastIndex));
}

function formatDelimitedLinks(value) {
  const source = String(value || '');
  const urlPattern = /(https?:\/\/[^\s"<>]+)/g;
  let html = '';
  let lastIndex = 0;
  for (const match of source.matchAll(urlPattern)) {
    html += formatDelimitedScriptureText(source.slice(lastIndex, match.index));
    html += formatSingleAutoLink(escapeHtml(match[0]));
    lastIndex = match.index + match[0].length;
  }
  return html + formatDelimitedScriptureText(source.slice(lastIndex));
}

function formatDelimitedCell(value) {
  const source = String(value || '');
  let html = '';
  let lastIndex = 0;
  for (const match of source.matchAll(/\*\*([^*\n]+)\*\*/g)) {
    html += formatDelimitedLinks(source.slice(lastIndex, match.index));
    html +=
      '<strong><span class="md-marker md-strong-marker">**</span>' +
      formatDelimitedLinks(match[1]) +
      '<span class="md-marker md-strong-marker">**</span></strong>';
    lastIndex = match.index + match[0].length;
  }
  return (html + formatDelimitedLinks(source.slice(lastIndex))).replace(/\n/g, '<br>');
}

function formatDelimitedTable(table, format) {
  const header = table.header.map((cell) => `<th scope="col">${formatDelimitedCell(cell)}</th>`).join('');
  const rows = table.rows
    .map((row) => '<tr>' + row.map((cell) => `<td>${formatDelimitedCell(cell)}</td>`).join('') + '</tr>')
    .join('');
  return (
    `<div class="md-table-wrap md-delimited-table-wrap"><table class="md-table md-delimited-table" data-delimited-format="${format}">` +
    `<thead><tr>${header}</tr></thead><tbody>${rows}</tbody></table></div>`
  );
}

function formatDelimitedSource(lines) {
  return `<pre class="md-delimited-source">${escapeHtml(lines.join('\n'))}</pre>`;
}

function parseMarkdownTableRow(line) {
  const source = String(line || '').trim();
  if (!source.includes('|')) {
    return null;
  }

  const cells = source
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split(/(?<!\\)\|/)
    .map((cell) => cell.trim().replace(/\\\|/g, '|'));
  return cells.length >= 2 ? cells : null;
}

function isMarkdownTableSeparator(cells) {
  return cells.length >= 2 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

const imageMarkdownPattern = /!\[([^\]\n]+)\]\(([^)\s]+)\)/;

export function hasImageMarkdown(str) {
  return imageMarkdownPattern.test(String(str || ''));
}

// 本文の中で最初に出てくる画像リンク。ラベルは空でもよい（`![](url)`）。
// カードビューが「ラベルと画像だけのカード」を描くために使う。
export function firstMarkdownImage(str) {
  const match = String(str || '').match(/!\[([^\]\n]*)\]\(([^)\s]+)\)/);
  if (!match) {
    return null;
  }
  const url = match[2];
  if (!isSafeMarkdownUrl(url)) {
    return null;
  }
  return { label: match[1].trim(), url };
}

export function hasMarkdownTable(str) {
  const lines = String(str || '').split(/\r?\n/);
  for (let index = 0; index < lines.length - 1; index += 1) {
    const header = parseMarkdownTableRow(lines[index]);
    const nextRow = parseMarkdownTableRow(lines[index + 1]);
    if (header && nextRow && header.length === nextRow.length) {
      return true;
    }
  }
  return false;
}

function formatMarkdownTable(header, separator, rows, options) {
  // セルの中は1行の文。行単位の装飾やチェックボックスは使わない
  // （チェックボックスは本文全体の通し番号で切り替えるため、セル内には置けない）。
  const cellOptions = { ...options, blockLines: false, decorateHeadingLines: false, decorateCheckboxes: false };
  const alignments = separator.map((cell) => {
    const starts = cell.startsWith(':');
    const ends = cell.endsWith(':');
    return starts && ends ? 'center' : ends ? 'right' : starts ? 'left' : '';
  });
  const renderCells = (cells, tag) =>
    cells
      .map((cell, index) => {
        const textAlign = tag === 'th' ? 'center' : alignments[index];
        const alignment = textAlign ? ` style="text-align: ${textAlign}"` : '';
        return `<${tag}${alignment}>${formatMarkdownInlineText(cell, cellOptions)}</${tag}>`;
      })
      .join('');

  return (
    '<div class="md-table-wrap"><table class="md-table"><thead><tr>' +
    renderCells(header, 'th') +
    '</tr></thead><tbody>' +
    rows.map((row) => `<tr>${renderCells(row, 'td')}</tr>`).join('') +
    '</tbody></table></div>'
  );
}

// 本文をHTMLにする。整形の入口はここ1つで、アウトライン・カード・公開画面が同じ結果を得る。
//
// - blockLines: 行単位の装飾（見出し `#`〜`######`・箇条書き `- ` `1. `・区切り線 `-----`）を行う。
//   表示用の設定。編集ボックスのように原文を直接編集する場所では使わない
// - decorateHeadingLines: 見出し行だけを装飾する（カードの編集ボックス用）
export function formatMarkdownInline(
  str,
  options: {
    inlineImages?: boolean;
    tables?: boolean;
    mermaid?: boolean;
    delimitedTables?: boolean;
    code?: boolean;
    decorateCheckboxes?: boolean;
    blockLines?: boolean;
    decorateHeadingLines?: boolean;
  } = {}
) {
  const source = String(str || '');
  const wantTables = options.tables !== false;
  const wantMermaid = options.mermaid !== false;
  const wantDelimitedTables = options.delimitedTables !== false;
  const wantCode = options.code !== false;
  if (
    (!wantTables || !hasMarkdownTable(source)) &&
    (!wantMermaid || !hasMermaidBlock(source)) &&
    (!wantDelimitedTables || !hasDelimitedFence(source)) &&
    (!wantCode || !hasCodeBlock(source))
  ) {
    return formatMarkdownInlineText(source, options);
  }

  const lines = source.split(/\r?\n/);
  const html = [];
  const textLines = [];
  // チェックボックスの通し番号のため、各行より前にいくつあるかを数えておく
  const checkboxesBeforeLine: number[] = [];
  let checkboxCount = 0;
  for (const line of lines) {
    checkboxesBeforeLine.push(checkboxCount);
    if (markdownCheckboxLinePattern.test(line)) {
      checkboxCount += 1;
    }
  }
  let textStartLine = 0;
  const flushText = () => {
    if (textLines.length > 0) {
      html.push(
        formatMarkdownInlineText(textLines.join('\n'), {
          ...options,
          checkboxBase: checkboxesBeforeLine[textStartLine] ?? 0,
        })
      );
      textLines.length = 0;
    }
  };

  for (let index = 0; index < lines.length; index += 1) {
    const mermaidMatch = wantMermaid ? mermaidFenceMatch(lines[index]) : null;
    if (mermaidMatch) {
      let closeIndex = -1;
      for (let scan = index + 1; scan < lines.length; scan += 1) {
        if (isMatchingFenceClose(lines[scan], mermaidMatch[1])) {
          closeIndex = scan;
          break;
        }
      }
      if (closeIndex !== -1) {
        flushText();
        html.push(formatMermaidBlock(lines.slice(index + 1, closeIndex).join('\n')));
        index = closeIndex;
        continue;
      }
    }

    if (wantDelimitedTables) {
      const match = delimitedFenceMatch(lines[index]);
      if (match) {
        let closeIndex = -1;
        for (let scan = index + 1; scan < lines.length; scan += 1) {
          if (isDelimitedFenceClose(lines[scan], match[1])) {
            closeIndex = scan;
            break;
          }
        }
        flushText();
        if (closeIndex === -1) {
          html.push(formatDelimitedSource(lines.slice(index)));
          break;
        }
        const format = match[2].toLowerCase();
        const delimiter = format === 'csv' ? ',' : '\t';
        const table = parseDelimitedTable(lines.slice(index + 1, closeIndex).join('\n'), delimiter);
        html.push(
          table ? formatDelimitedTable(table, format) : formatDelimitedSource(lines.slice(index, closeIndex + 1))
        );
        index = closeIndex;
        continue;
      }
    }

    if (wantCode) {
      const match = codeFenceMatch(lines[index]);
      if (match) {
        let closeIndex = -1;
        for (let scan = index + 1; scan < lines.length; scan += 1) {
          if (isMatchingFenceClose(lines[scan], '```')) {
            closeIndex = scan;
            break;
          }
        }
        flushText();
        // 閉じフェンスがないときは、残りの行をまとめてコードとして表示する。
        const endIndex = closeIndex === -1 ? lines.length : closeIndex;
        html.push(formatCodeBlock(match[1], lines.slice(index + 1, endIndex)));
        index = endIndex;
        continue;
      }
    }

    if (wantTables) {
      const header = parseMarkdownTableRow(lines[index]);
      const secondLine = parseMarkdownTableRow(lines[index + 1]);
      if (header && secondLine && header.length === secondLine.length) {
        flushText();
        const hasSeparator = isMarkdownTableSeparator(secondLine);
        const separator = hasSeparator ? secondLine : header.map(() => '---');
        const rows = [];
        index += hasSeparator ? 2 : 1;
        while (index < lines.length) {
          const row = parseMarkdownTableRow(lines[index]);
          if (!row || row.length !== header.length) {
            break;
          }
          rows.push(row);
          index += 1;
        }
        html.push(formatMarkdownTable(header, separator, rows, options));
        index -= 1;
        continue;
      }
    }

    if (textLines.length === 0) {
      textStartLine = index;
    }
    textLines.push(lines[index]);
  }

  flushText();
  return html.join('');
}

// ノード本文を、一覧やラベルに出すための1行テキストへ整える。
// 最初の非空行だけを取り、意味を持たないマークダウン記法を落とす。
export function stripMarkdownForLabel(text) {
  const firstLine =
    String(text || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .find((line) => line !== '') || '';

  return firstLine
    .replace(/!?\[([^\]\n]*)\]\([^)\s]*\)/g, '$1') // リンク・画像はラベル部分だけ残す
    .replace(/^#{1,6}\s+/, '')
    .replace(/^[>＞]\s*/, '')
    .replace(/^[-*+]\s+/, '')
    .replace(/^\[[ vx]\]\s*/i, '')
    .replace(/\*\*/g, '')
    .replace(/`/g, '')
    .replace(/[\u0000-\u001f\u007f]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// 1行ラベルを指定文字数で切り詰める。切り詰めたことが分かるように末尾に「…」を付ける。
export function truncateLabel(text, limit) {
  const source = String(text || '');
  const chars = Array.from(source);
  return chars.length > limit ? `${chars.slice(0, limit).join('')}…` : source;
}

export function oneLineLabel(text, limit = 40) {
  return truncateLabel(stripMarkdownForLabel(text), limit);
}
